import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ProfessionalService } from '../../services/professional.service';
import { ProfessionalStateService } from '../../services/professional-state.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss']
})
export class MainLayoutComponent implements OnInit, OnDestroy {

  user: any = null;
  professionals: any[] = [];
  selectedProfessional: any = null;
  loadingProfessionals = true;
  sidebarOpen = false;
  showProfSwitcher = false;
  financialMenuOpen = false;

  showModal      = false;
  savingProf     = false;
  modalTab: 'dados' | 'pagamento' = 'dados';

  profForm: any = {
    name: '', specialty: '', professionalId: '', email: '', phone: '', notes: '',
    address: '', number: '', neighborhood: '', complement: '', city: '', state: '', zipCode: ''
  };
  billingForm: any = {
    preferredPaymentMethod: 1,
    pixKey: '', pixKeyType: 3, pixHolderName: '',
    cardHolderName: '', cardLastFourDigits: '', cardBrand: '',
    cardExpiryMonth: '', cardExpiryYear: '', isConfigured: false
  };
  cardNumberDisplay = '';
  cardExpiry        = '';

  private subs = new Subscription();

  constructor(
    private auth: AuthService,
    private profService: ProfessionalService,
    private profState: ProfessionalStateService,
    public router: Router,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.user = this.auth.getCurrentUser();
    this.loadProfessionals();
    this.subs.add(
      this.profState.openModal$.subscribe(() => this.openModal())
    );
    this.subs.add(
      this.profState.selected$.subscribe(p => this.selectedProfessional = p)
    );
    this.subs.add(
      this.profState.list$.subscribe(list => this.professionals = list)
    );
    // Auto-expandir menu financeiro se estiver em rota financeira
    if (this.isFinancialRoute()) {
      this.financialMenuOpen = true;
    }
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  loadProfessionals(): void {
    this.loadingProfessionals = true;
    this.profState.setLoading(true);
    this.profService.getAll().subscribe({
      next: (list) => {
        this.loadingProfessionals = false;
        this.profState.setList(list);
        if (list.length > 0) this.selectProfessional(list[0]);
      },
      error: () => {
        this.loadingProfessionals = false;
        this.profState.setLoading(false);
      }
    });
  }

  // ── Papel do usuário ────────────────────────────────────────────
  get isAdmin(): boolean {
    return this.user?.isAdmin === true;
  }

  /** Usuário é o profissional dono de exatamente 1 cadastro (seu próprio) */
  get isSingleOwner(): boolean {
    return !this.isAdmin &&
           this.professionals.length === 1 &&
           this.professionals[0]?.isOwner === true;
  }

  /** Pode trocar de profissional (múltiplos ou admin) */
  get canSwitchProfessional(): boolean {
    return this.isAdmin || this.professionals.length > 1;
  }

  /** Pode acessar a tela de gerenciamento de profissionais */
  get canManageProfessionals(): boolean {
    return this.isAdmin || this.professionals.some((p: any) => p.isOwner);
  }

  selectProfessional(p: any): void {
    this.profState.select(p);
  }

  switchProfessional(p: any): void {
    this.showProfSwitcher = false;
    if (this.selectedProfessional?.id === p.id) return;
    this.profState.select(p);
    this.router.navigate(['/dashboard']);
  }

  openModal(): void {
    this.profForm    = { name: '', specialty: '', professionalId: '', email: '', phone: '', notes: '',
                         address: '', number: '', neighborhood: '', complement: '', city: '', state: '', zipCode: '' };
    this.billingForm = {
      preferredPaymentMethod: 1, pixKey: '', pixKeyType: 3, pixHolderName: '',
      cardHolderName: '', cardLastFourDigits: '', cardBrand: '',
      cardExpiryMonth: '', cardExpiryYear: '', isConfigured: false
    };
    this.cardNumberDisplay = '';
    this.cardExpiry        = '';
    this.modalTab          = 'dados';
    this.showModal         = true;
  }

  closeModal(): void { this.showModal = false; }

  saveProf(): void {
    if (!this.profForm.name?.trim()) { this.notification.warning('Campo obrigatório', 'Nome é obrigatório'); return; }

    if (this.billingForm.preferredPaymentMethod === 1 && this.billingForm.pixKey)
      this.billingForm.isConfigured = true;
    if (this.billingForm.preferredPaymentMethod === 2 && this.cardNumberDisplay) {
      this.billingForm.cardLastFourDigits = this.cardNumberDisplay.replace(/\s/g, '').slice(-4);
      this.billingForm.isConfigured       = true;
    }

    this.savingProf = true;
    this.profService.create(this.profForm).subscribe({
      next: (created: any) => {
        if (this.billingForm.isConfigured) {
          this.profService.saveBilling(created.id, this.billingForm).subscribe({ error: () => {} });
        }
        this.profState.addToList(created);
        this.selectProfessional(created);
        this.closeModal();
        this.savingProf = false;
        this.notification.success('Cadastrado!', `${created.name} cadastrado! Trial de 30 dias iniciado — R$ 39,90/mês após o período.`);
      },
      error: () => { this.savingProf = false; this.notification.error('Erro', 'Erro ao cadastrar profissional'); }
    });
  }

  formatCard(e: Event): void {
    let v = (e.target as HTMLInputElement).value.replace(/\D/g, '').substring(0, 16);
    this.cardNumberDisplay = v.replace(/(.{4})/g, '$1 ').trim();
    if      (v.startsWith('4')) this.billingForm.cardBrand = 'Visa';
    else if (v.startsWith('5')) this.billingForm.cardBrand = 'Mastercard';
    else if (v.startsWith('3')) this.billingForm.cardBrand = 'Amex';
    else                        this.billingForm.cardBrand = '';
  }

  formatExpiry(e: Event): void {
    let v = (e.target as HTMLInputElement).value.replace(/\D/g, '').substring(0, 4);
    if (v.length >= 2) v = v.substring(0, 2) + '/' + v.substring(2);
    this.cardExpiry = v;
    if (v.length === 5) {
      this.billingForm.cardExpiryMonth = v.substring(0, 2);
      this.billingForm.cardExpiryYear  = '20' + v.substring(3);
    }
  }

  pixPlaceholder(): string {
    const m: any = { 1: '000.000.000-00', 2: '00.000.000/0001-00',
                     3: 'email@exemplo.com', 4: '(11) 98765-4321', 5: 'chave-aleatória' };
    return m[this.billingForm.pixKeyType] || '';
  }

  logout():            void { this.auth.logout(); }
  navigate(r: string): void { this.router.navigate([r]); this.sidebarOpen = false; }

  toggleFinancialMenu(): void {
    this.financialMenuOpen = !this.financialMenuOpen;
  }

  isFinancialRoute(): boolean {
    const financialRoutes = [
      '/suppliers', '/account-categories', '/cost-centers',
      '/bank-accounts', '/health-insurances', '/accounts-receivable', '/accounts-payable'
    ];
    return financialRoutes.some(route => this.router.url.includes(route));
  }

  openMyProfile(): void {
    this.sidebarOpen = false;
    this.showModal = false; // Fecha qualquer modal aberto em main-layout
    this.router.navigate(['/professionals']).then(() => {
      // Usa um timeout para garantir que o componente foi carregado
      setTimeout(() => this.profState.requestModal(), 100);
    });
  }

  greeting(): string {
    const h = new Date().getHours();
    return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
  }

  daysRemaining(date?: string): number {
    if (!date) return 0;
    return Math.max(0, Math.ceil((new Date(date).getTime() - Date.now()) / 86400000));
  }

  subLabel(status?: number): string {
    const m: any = { 1: '🆓 Trial', 2: '✅ Ativo', 3: '⚠️ Atrasado', 4: '🚫 Suspenso', 5: 'Cancelado' };
    return m[status!] || '';
  }

  subClass(status?: number): string {
    const m: any = { 1: 'bg-info', 2: 'bg-success', 3: 'bg-warning text-dark', 4: 'bg-danger', 5: 'bg-secondary' };
    return `badge ${m[status!] || 'bg-secondary'}`;
  }

  formatPhone(value: string): string {
    const v = value.replace(/\D/g, '').substring(0, 11);
    if (v.length === 0) return '';
    if (v.length <= 2)  return `(${v}`;
    if (v.length <= 6)  return `(${v.substring(0, 2)}) ${v.substring(2)}`;
    if (v.length <= 10) return `(${v.substring(0, 2)}) ${v.substring(2, 6)}-${v.substring(6)}`;
    return `(${v.substring(0, 2)}) ${v.substring(2, 7)}-${v.substring(7)}`;
  }

  onProfPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.formatPhone(input.value);
    this.profForm.phone = formatted;
    input.value = formatted;
  }
}
